import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  useGetAllInvoiceQuery,
  useCreateEmailInvoiceMutation } from "../../../Api/attoDeskApi";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate } from "react-router";
import {
  ICompanyDetails,
  ICustomer,
  ICustomerProject,
} from "../../../Api/Interface/api.interface";
import dayjs, { Dayjs } from "dayjs";
import {
  ProductType,
  TransferType,
  WorkOrderStatus,
} from "../../../Core/Enum/enum";
import { useParams } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import LocalPrintshopSharpIcon from "@mui/icons-material/LocalPrintshopSharp";
import EmailIcon from "@mui/icons-material/Email";
import { useNotifier } from "../../../Core/Notifier";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import CustomerTransaction from "..";
import moment from 'moment';
import axios from "axios";

const getStatusLabel = (statusCode: number): string => {
  switch (statusCode) {
    case WorkOrderStatus.Paid:
      return "Paid";
    case WorkOrderStatus.Cancel:
      return "Canceled";
    case WorkOrderStatus.Pending:
      return "Pending";
      case WorkOrderStatus.Initiated:
        return "Initiated";
    default:
      return "Unknown Status";
  }
};


const getTransferType = (statusCode: string): string => {
  switch (statusCode) {
    case TransferType.OneWayTransfer:
      return "One Way Transfer";
    case TransferType.RoundTrip:
      return "Round Trip";
    case TransferType.MultiDestination:
      return "Multi Destination";
    default:
      return "Unknown Status";
  }
};

interface IProduct {
  productName: string;
  productDescription: string;
  productImg: String | null;
  numberOfPassengers: number | null;
  transferType: string;
  transferTypeFrom: string;
  transferTypeTo: string;
  transferTypeDescription: string;
  departureDateTime: Dayjs | null;
  returnDateTime: Dayjs | null;
  departureAddress: string;
  destinationAddress: string;
  multiDestination: string;
  quantity: number | null;
  busCount?: number | null;
  price: number | null;
  tax: number | null;
  taxAmount: number | null;
  total: number;
  taxId: number | null;
  unitPriceWithDiscount: number | null;
  unitTotalWithDiscount: number;
  productType: string;
  productLongDescription: string;
  productShortDescription: string;
}

interface IDiscount {
  value: number | null;
  description: string;
}

interface IQuoteWorkOrder {
  id: number;
  quoteWorkOrderCompanyId: number;
  quoteWorkOrderCustomerId: string;
  quoteWorkOrderCustomerProjectId: string;
  quoteWorkOrderNo: string;
  quoteWorkOrderIssueDate: Dayjs;
  quoteWorkOrderDueDate: Dayjs;
  quoteTripId: string;
  quoteTripName: string;
  quoteWorkOrderAmount: number | null;
  quoteWorkOrderStatus: number;
  quoteWorkOrderSale: IProduct[];
  quoteWorkOrderDiscount: string;
  quoteWorkOrderSubTotal: string;
  quoteWorkOrderSaleGrandTotal: string;
  quoteWorkOrderSaleDiscountDescription: IDiscount[];
  customer: ICustomer;
  company: ICompanyDetails;
  project: ICustomerProject;
  quoteWorkOrderCustomerRemark: string;
  quoteWorkOrderCustomerMemo: string;
  quoteWorkOrderSaleDiscount: string;
  quoteWorkOrderSaleSubTotal: string;
  created_by: number;
  created_at: string;
  mainStatus: number;
  invoiceId: number;
  InvoiceNo: string;
  amount_paid: number;
  po_number: string;
  work_order_id: string;
}

interface  Payment {
  id: number;
  transaction_id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  payment_mode: string;
  description: string;
  file_path?: string;
  created_at: string;
  updated_at: string;
  date: string;
  is_return: number;
  is_deleted: number;
}

interface ViewInvoiceProps {
    invoiceId: string;
    type: string;
}

const ViewInvoice: React.FC<ViewInvoiceProps> = ({ invoiceId, type}) => {

  const classes = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const { id: InvoiceId } = useParams<{ id: string }>();
  useEffect(() => {}, [InvoiceId]);
  const componentRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [newEmailInvoice, { isLoading: isEmailSending }] = useCreateEmailInvoiceMutation();
  const { showErrorMessage, showMessage } = useNotifier();

  const [dataLoaded, setDataLoaded] = useState(false);
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [payments, setPayments] = useState<Payment[]>([]);
  const API_URL = process.env.REACT_APP_API_URL;


  const {
    data: workOrderData,
    isLoading: workIsLoading,
    isError: workOrderError,
    refetch: workOrderRefetch,
  } = useGetAllInvoiceQuery({
    searchText: "",
    id: String(invoiceId),
    quoteWorkOrderCustomerId: "",
    quoteWorkOrderCustomerProjectId: "",
  });

  const WorkOrderDataList = useMemo(() => {
    if (
      workOrderData &&
      Array.isArray(workOrderData.data) &&
      workOrderData.data.length > 0
    ) {
      return workOrderData.data[0] as IQuoteWorkOrder;
    }
    return null;
  }, [workOrderData]);

  const formatAddressReadOnly = (
    streetNumber: string,
    streetName: string,
    city: string,
    province: string,
    postalCode: string,
  ) => {
    return `${streetNumber} ${streetName}, ${city}, ${province}, ${postalCode}`;
  };

  const customerFormatAddressReadOnly = (
    streetNumber: string,
    streetName: string,
    city: string,
    province: string,
    postalCode: string
  ) => {
    const line1 = [streetNumber, streetName].filter(Boolean).join(" ");
    const line2 = [city, province, postalCode].filter(Boolean).join(", ");
    return [line1, line2].filter(Boolean).join("\n");
  };

  const projectFormatAddressReadOnly = (
    streetNumber: string,
    streetName: string,
    city: string,
    province: string,
    postalCode: string,
  ) => {
    return `${streetNumber} ${streetName},\n ${city}, ${province}, ${postalCode}`;
  };

  const initialCompanyAddress = formatAddressReadOnly(
    WorkOrderDataList?.company.companyStreetNumber || "",
    WorkOrderDataList?.company.companyStreetName || "",
    WorkOrderDataList?.company.companyCity || "",
    WorkOrderDataList?.company.companyProvince || "",
    WorkOrderDataList?.company.companyPostalCode || "",
  );

  const initialCustomerAddress = customerFormatAddressReadOnly(
    WorkOrderDataList?.customer.customerStreetNumber || "",
    WorkOrderDataList?.customer.customerStreetName || "",
    WorkOrderDataList?.customer.customerCity || "",
    WorkOrderDataList?.customer.customerProvince || "",
    WorkOrderDataList?.customer.customerPostalCode || "",
  );

  const initialProjectAddress = projectFormatAddressReadOnly(
    WorkOrderDataList?.project?.projectStreetNumber || "",
    WorkOrderDataList?.project?.projectStreetName || "",
    WorkOrderDataList?.project?.projectCity || "",
    WorkOrderDataList?.project?.projectProvince || "",
    WorkOrderDataList?.project?.projectPostalCode || "",
  );

  const coursings = Array.isArray(WorkOrderDataList?.quoteWorkOrderSale)
    ? WorkOrderDataList!.quoteWorkOrderSale
    : [];

  const discountList = Array.isArray(
    WorkOrderDataList?.quoteWorkOrderSaleDiscountDescription
  )
    ? WorkOrderDataList!.quoteWorkOrderSaleDiscountDescription
    : [];

  useEffect(() => {
    if (WorkOrderDataList && WorkOrderDataList.project) {
      setDataLoaded(true);
    } else {
      setDataLoaded(false);
    }
  }, [workOrderData]);

  useEffect(() => {
    fetchPayments();
  }, [WorkOrderDataList?.work_order_id]);

  const fetchPayments = () => {
    if (!WorkOrderDataList?.work_order_id) return;
    axios.get(`${API_URL}/payments-by-workorder?work_order_id=${WorkOrderDataList?.work_order_id}`)
      .then(response => {
        const sortedPayments = response.data.sort((a: Payment, b: Payment) => {
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        const validPayments = sortedPayments.filter((payment: Payment) => payment.is_deleted === 0 && payment.is_return === 0);
        setPayments(validPayments);
      })
      .catch(error => {
        console.error("Error fetching payments:", error);
      });
  };

  const handlePrint = async () => {
    try {
      const response = await axios.get(
        `${LARAVEL_API_URL}/invoices/${invoiceId}/pdf`,
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      showErrorMessage('Failed to generate invoice PDF.');
    }
  };

  const handleSendEmail = async () => {
    try {
      if (!componentRef.current) {
        showErrorMessage("The invoice component is not available.");
        return;
      }
      if (!dataLoaded) {
        showErrorMessage("Data is still loading. Please wait.");
        return;
      }
      if (!WorkOrderDataList || !WorkOrderDataList.project) {
        showErrorMessage("Required work order or project data is missing.");
        return;
      }
      // Convert every img src to a base64 data URL so html2canvas can render
      // them without CORS issues (authenticated URLs would otherwise be blocked).
      const imgs = Array.from(componentRef.current.querySelectorAll('img'));
      await Promise.all(
        imgs.map(async (img) => {
          const src = img.getAttribute('src') || '';
          if (!src || src.startsWith('data:')) return;
          try {
            const res = await axios.get(src, {
              responseType: 'blob',
              headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
            });
            const dataUrl = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onload = (e) => resolve(e.target?.result as string);
              reader.readAsDataURL(res.data);
            });
            img.src = dataUrl;
            await new Promise<void>((resolve) => {
              if (img.complete && img.naturalWidth > 0) { resolve(); return; }
              img.onload = () => resolve();
              img.onerror = () => resolve();
            });
          } catch { /* leave original src and continue */ }
        })
      );

      const pdf = new jsPDF();
      const canvas = await html2canvas(componentRef.current, { useCORS: true, logging: false });
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      const pdfBlob = pdf.output('blob');
      const fileName = `${WorkOrderDataList?.InvoiceNo}.pdf`;
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
      const formData = new FormData();
      formData.append('customer_id', WorkOrderDataList?.project.customerId.toString() || '');
      formData.append('project_id', WorkOrderDataList?.project.id.toString() || '');
      formData.append('to', WorkOrderDataList?.project.projectEmail || '');
      formData.append('subject', "Invoice for Your Project");
      formData.append('message', "Please find attached the invoice for your project.");
      formData.append('invoice', file);
      const emailResponse = await newEmailInvoice(formData).unwrap();
      if (!emailResponse) {
        showErrorMessage("Failed to send email ");
      } else {
        console.log('Email sent successfully:', emailResponse.data);
        showMessage("Email sent successfully");
      }
    } catch (error) {
      console.error("Error sending email:", error);
    }
  };

  useEffect(() => {
    let newTotalPrice = 0;
    let newTotalTax = 0;
    coursings.forEach((p) => {
      const lineTotal = p.total > 0 ? p.total : (p.price ?? 0) * (p.busCount ?? p.quantity ?? 1);
      newTotalPrice += lineTotal;
      newTotalTax += p.taxAmount ?? 0;
    });
    setTotalPrice(newTotalPrice);
    setTotalTax(newTotalTax);
  }, [coursings]);

  // ─── Render ───────────────────────────────────────────────────────────────
  const grandTotal = parseFloat(
    WorkOrderDataList?.quoteWorkOrderSaleGrandTotal || "0"
  );
  const totalDiscount = parseFloat(
    WorkOrderDataList?.quoteWorkOrderDiscount || "0"
  );

  const totalPayments = payments.reduce((sum, p) => sum + Number(p.amount), 0);
  const balanceDue = Math.max(0, grandTotal - totalPayments);

  const date = WorkOrderDataList?.quoteWorkOrderIssueDate
    ? dayjs(WorkOrderDataList.quoteWorkOrderIssueDate).format("D MMM YYYY")
    : "";

  return (
    <Box>
      {/* ── Action Bar ─────────────────────────────────────────────────── */}
      {type !== "email" && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 2,
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            startIcon={<LocalPrintshopSharpIcon />}
            onClick={handlePrint}
            sx={{
              background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
              color: "#fff",
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(30,41,59,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #334155 0%, #1e293b 100%)",
              },
            }}
          >
            Print
          </Button>
        </Box>
      )}

      {/* ── Document ───────────────────────────────────────────────────── */}
      <div ref={componentRef}>
        <div style={{
          backgroundColor: "#ffffff",
          color: "#000000",
          fontFamily: "Arial, sans-serif",
          fontSize: "13px",
          width: "100%",
          boxSizing: "border-box" as const,
          margin: "0",
          padding: "0",
          position: "relative",
        }}>
          {/* ── Watermark ─────────────────────────────────────────────── */}
          {WorkOrderDataList?.company.CompanyWatermark && (
            <div style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "60%",
              opacity: 0.06,
              pointerEvents: "none",
              zIndex: 0,
            }}>
              <img
                src={`${LARAVEL_API_URL}/${WorkOrderDataList.company.CompanyWatermark}`}
                alt="Watermark"
                style={{ width: "100%" }}
              />
            </div>
          )}

          {/* ── Header ────────────────────────────────────────────────── */}
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            padding: "24px 32px 16px 32px",
            borderBottom: "2px solid #000000",
            position: "relative",
            zIndex: 1,
          }}>
            {/* Left half: Logo */}
            <div style={{ width: "50%", display: "flex", alignItems: "center" }}>
              {WorkOrderDataList?.company?.CompanyLogo ? (
                <img
                  src={`${LARAVEL_API_URL}/${WorkOrderDataList.company.CompanyLogo}`}
                  alt="Company Logo"
                  style={{ maxWidth: "100%", height: "auto", minHeight: "80px", objectFit: "contain", objectPosition: "left center", display: "block" }}
                />
              ) : (
                <div style={{
                  width: "80px", height: "80px", borderRadius: "50%",
                  background: "#2f3891",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "36px", fontWeight: "bold", color: "#fff",
                }}>
                  {(WorkOrderDataList?.company?.companyName || "C").charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Right half: INVOICE title + company info + doc reference */}
            <div style={{ width: "50%", textAlign: "right" }}>
              {/* INVOICE title */}
              <div style={{
                fontSize: "26px",
                fontWeight: "bold",
                letterSpacing: "3px",
                textTransform: "uppercase",
                marginBottom: "10px",
                lineHeight: 1.1,
              }}>
                INVOICE
              </div>
              {/* Company address block */}
              <div style={{ marginBottom: "8px", fontSize: "13px" }}>
                <div style={{ fontWeight: "bold" }}>{initialCompanyAddress}</div>
                {WorkOrderDataList?.company.companyMobileNumber && (
                  <div style={{ fontWeight: "bold" }}>
                    Tel:{WorkOrderDataList.company.companyMobileNumber}
                  </div>
                )}
                {WorkOrderDataList?.company.companyEmail && (
                  <div style={{ fontWeight: "bold" }}>
                    {WorkOrderDataList.company.companyEmail}
                    {WorkOrderDataList?.company.companyWebSiteLink && (
                      <span style={{ marginLeft: "8px" }}>
                        {WorkOrderDataList.company.companyWebSiteLink}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {/* Invoice reference */}
              <div style={{ fontSize: "13px" }}>
                {WorkOrderDataList?.InvoiceNo && (
                  <div><strong>Charter #{WorkOrderDataList.InvoiceNo}</strong></div>
                )}
                {date && (
                  <div>Invoice Date: {date}</div>
                )}
                {WorkOrderDataList?.po_number && (
                  <div>PO No: {WorkOrderDataList.po_number}</div>
                )}
              </div>
            </div>
          </div>

          {/* ── Client Details + Pickup/Dropoff/Times ─────────────────── */}
          <div style={{ padding: "16px 32px", position: "relative", zIndex: 1 }}>
            {/* Company Name */}
            {WorkOrderDataList?.quoteWorkOrderCustomerProjectId ? (
              <div style={{ marginBottom: "12px" }}>
                <div><strong>Company Name: {WorkOrderDataList?.project?.projectName || ""}</strong></div>
                {(WorkOrderDataList?.project?.projectPhoneNumber || WorkOrderDataList?.project?.projectEmail) && (
                  <div>
                    {[WorkOrderDataList.project.projectPhoneNumber, WorkOrderDataList.project.projectEmail].filter(Boolean).join(" | ")}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginBottom: "12px" }}>
                <div><strong>Company Name: {[
                  ((WorkOrderDataList?.customer?.customerFirstName || "").charAt(0).toUpperCase() + (WorkOrderDataList?.customer?.customerFirstName || "").slice(1).toLowerCase()),
                  ((WorkOrderDataList?.customer?.customerLastName || "").charAt(0).toUpperCase() + (WorkOrderDataList?.customer?.customerLastName || "").slice(1).toLowerCase()),
                ].filter(Boolean).join(" ")}</strong></div>
                {[WorkOrderDataList?.customer?.customerMobileNum, WorkOrderDataList?.customer?.customerEmail].filter(Boolean).length > 0 && (
                  <div>{[WorkOrderDataList?.customer?.customerMobileNum, WorkOrderDataList?.customer?.customerEmail].filter(Boolean).join(" | ")}</div>
                )}
              </div>
            )}

            {/* Per-product: pickup / dropoff */}
            {coursings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((product, index) => {
              const fmtStopTime = (val: any) => {
                if (!val) return '';
                const d = dayjs(val);
                return d.isValid() ? d.format('MMM D, YYYY h:mm A') : '';
              };
              return (
                <div key={index} style={{ marginBottom: "10px" }}>
                  {/* Pick Up */}
                  {((product as any).pickups?.length > 0) ? (
                    <div style={{ marginBottom: "4px" }}>
                      <strong>PICK UP:</strong>
                      {(product as any).pickups.map((pickup: any, pi: number) => {
                        const ordinals = ["1st", "2nd", "3rd", "4th", "5th"];
                        const label = (product as any).pickups.length > 1
                          ? `${ordinals[pi] || `${pi + 1}th`} Pickup`
                          : "Pickup";
                        const t = fmtStopTime(pickup.dateTime ?? pickup.time ?? null);
                        const addr = [pickup.address, pickup.city].filter(Boolean).join(', ');
                        return addr ? (
                          <div key={pi} style={{ marginLeft: "16px" }}>
                            {label} {"\u2013"} {addr}{t ? ` \u00B7 ${t}` : ''}
                          </div>
                        ) : null;
                      })}
                    </div>
                  ) : product.departureAddress ? (
                    <div style={{ marginBottom: "4px" }}>
                      <strong>PICK UP:</strong>
                      <span style={{ marginLeft: "8px" }}>
                        Pickup {"\u2013"} {product.departureAddress}
                        {fmtStopTime(product.departureDateTime) ? ` \u00B7 ${fmtStopTime(product.departureDateTime)}` : ''}
                      </span>
                    </div>
                  ) : null}

                  {/* Drop Off */}
                  {((product as any).dropoffs?.length > 0) ? (
                    <div style={{ marginBottom: "4px" }}>
                      <strong>DROP OFF:</strong>
                      {(product as any).dropoffs.map((drop: any, di: number) => {
                        const ordinals = ["1st", "2nd", "3rd", "4th", "5th"];
                        const label = (product as any).dropoffs.length > 1
                          ? `${ordinals[di] || `${di + 1}th`} Dropoff`
                          : "Dropoff";
                        const t = fmtStopTime(drop.dateTime ?? drop.time ?? null);
                        const addr = [drop.address, drop.city].filter(Boolean).join(', ');
                        return addr ? (
                          <div key={di} style={{ marginLeft: "16px" }}>
                            {label} {"\u2013"} {addr}{t ? ` \u00B7 ${t}` : ''}
                          </div>
                        ) : null;
                      })}
                    </div>
                  ) : product.destinationAddress ? (
                    <div style={{ marginBottom: "4px" }}>
                      <strong>DROP OFF:</strong>
                      <span style={{ marginLeft: "8px" }}>
                        Dropoff {"\u2013"} {product.destinationAddress}
                        {fmtStopTime(product.returnDateTime) ? ` \u00B7 ${fmtStopTime(product.returnDateTime)}` : ''}
                      </span>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          {/* ── Services Table ────────────────────────────────────────── */}
          <div style={{ padding: "0 32px 16px 32px", position: "relative", zIndex: 1 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: "82%" }} />
                <col style={{ width: "18%" }} />
              </colgroup>
              <thead>
                <tr style={{ backgroundColor: "#f0f0f0" }}>
                  <th style={{
                    textAlign: "left",
                    padding: "8px 16px",
                    border: "1px solid #cccccc",
                    fontWeight: "bold",
                  }}>
                    Description
                  </th>
                  <th style={{
                    textAlign: "right",
                    padding: "8px 16px",
                    border: "1px solid #cccccc",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}>
                    Charges
                  </th>
                </tr>
              </thead>
              <tbody>
                {workOrderError || coursings.length === 0 ? (
                  <tr>
                    <td colSpan={2} style={{ textAlign: "center", padding: "16px", color: "#888" }}>
                      {workOrderError ? "Error fetching data" : "No data available"}
                    </td>
                  </tr>
                ) : (
                  coursings
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((product, index) => (
                      <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa" }}>
                        <td style={{ padding: "8px 16px", border: "1px solid #cccccc", verticalAlign: "top" }}>
                          <div style={{ fontWeight: "bold" }}>{product.productName}</div>
                          {(product.busCount || product.quantity || product.numberOfPassengers) && (
                            <div style={{ color: "#555", fontSize: "12px", marginTop: "2px" }}>
                              {product.busCount
                                ? `Qty: ${product.busCount} bus${product.busCount > 1 ? "es" : ""}`
                                : product.quantity
                                ? `Qty: ${product.quantity}`
                                : ""}
                              {(product.busCount || product.quantity) && product.numberOfPassengers ? "  ·  " : ""}
                              {product.numberOfPassengers ? `${product.numberOfPassengers} passengers` : ""}
                            </div>
                          )}
                          {product.productDescription && (
                            <div style={{ color: "#555", marginTop: "2px" }}>
                              {product.productDescription}
                            </div>
                          )}
                        </td>
                        <td style={{
                          padding: "8px 16px",
                          border: "1px solid #cccccc",
                          textAlign: "right",
                          verticalAlign: "top",
                          whiteSpace: "nowrap",
                        }}>
                          {/* Line total = stored total or price × busCount/quantity */}
                          <div style={{ fontWeight: "bold" }}>
                            ${(product.total > 0
                              ? product.total
                              : (product.price ?? 0) * (product.busCount ?? product.quantity ?? 1)
                            ).toFixed(2)}
                          </div>
                          {/* Per-bus breakdown if busCount > 1 */}
                          {(product.busCount ?? 1) > 1 && (product.price ?? 0) > 0 && (
                            <div style={{ fontSize: "11px", color: "#666", marginTop: "2px" }}>
                              ${(product.price ?? 0).toFixed(2)} × {product.busCount} buses
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Totals ────────────────────────────────────────────────── */}
          <div style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "0 32px 20px 32px",
            position: "relative",
            zIndex: 1,
          }}>
            <table style={{ fontSize: "13px", borderCollapse: "collapse", minWidth: "260px" }}>
              <tbody>
                <tr>
                  <td style={{ padding: "4px 12px", textAlign: "right" }}>AMOUNT</td>
                  <td style={{ padding: "4px 12px", textAlign: "right", borderBottom: "1px solid #ccc" }}>
                    ${totalPrice.toFixed(2)}
                  </td>
                </tr>
                {discountList.map((discount, index) => (
                  <tr key={index}>
                    <td style={{ padding: "4px 12px", textAlign: "right" }}>
                      {discount.description || "Discount"}
                    </td>
                    <td style={{ padding: "4px 12px", textAlign: "right", borderBottom: "1px solid #ccc" }}>
                      -${(discount.value ?? 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
                {totalDiscount !== 0 && (
                  <tr>
                    <td style={{ padding: "4px 12px", textAlign: "right" }}>TOTAL DISCOUNT</td>
                    <td style={{ padding: "4px 12px", textAlign: "right", borderBottom: "1px solid #ccc" }}>
                      -${totalDiscount.toFixed(2)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td style={{ padding: "4px 12px", textAlign: "right" }}>HST TAX</td>
                  <td style={{ padding: "4px 12px", textAlign: "right", borderBottom: "1px solid #ccc" }}>
                    ${totalTax.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: "6px 12px", textAlign: "right", fontWeight: "bold", borderTop: "2px solid #000" }}>
                    TOTAL
                  </td>
                  <td style={{ padding: "6px 12px", textAlign: "right", fontWeight: "bold", borderTop: "2px solid #000" }}>
                    ${grandTotal.toFixed(2)}
                  </td>
                </tr>
                {/* Payment history rows */}
                {payments.length > 0 && payments.map((payment) => (
                  <tr key={payment.id}>
                    <td style={{ padding: "4px 12px", textAlign: "right", color: "#475569" }}>
                      Payment - {payment.payment_method} {moment(payment.payment_date).format("DD-MMM-YY")}
                    </td>
                    <td style={{ padding: "4px 12px", textAlign: "right", borderBottom: "1px solid #ccc", color: "#475569" }}>
                      -${Number(payment.amount).toFixed(2)}
                    </td>
                  </tr>
                ))}
                {/* Balance Due */}
                <tr>
                  <td style={{ padding: "6px 12px", textAlign: "right", fontWeight: "bold", borderTop: "2px solid #000" }}>
                    BALANCE DUE
                  </td>
                  <td style={{ padding: "6px 12px", textAlign: "right", fontWeight: "bold", borderTop: "2px solid #000" }}>
                    ${balanceDue.toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── HST Number ────────────────────────────────────────────── */}
          {WorkOrderDataList?.company.companyHstNo && (
            <div style={{ padding: "0 32px 12px 32px", position: "relative", zIndex: 1 }}>
              <span>HST;{WorkOrderDataList.company.companyHstNo}</span>
            </div>
          )}

          {/* ── Comments (Special Notification) ───────────────────────── */}
          {WorkOrderDataList?.quoteWorkOrderCustomerRemark &&
            WorkOrderDataList.quoteWorkOrderCustomerRemark.trim() !== "" && (
              <div style={{ padding: "0 32px 16px 32px", position: "relative", zIndex: 1 }}>
                <div style={{ marginBottom: "8px" }}>
                  <strong>Comments:</strong>
                </div>
                <div style={{ marginBottom: "6px" }}>
                  {WorkOrderDataList.quoteWorkOrderCustomerRemark}
                </div>
              </div>
            )}

          {/* ── Signature Lines ───────────────────────────────────────── */}
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "48px 32px 24px 32px",
            position: "relative",
            zIndex: 1,
          }}>
            <div style={{ width: "45%", borderTop: "1px dotted #666", paddingTop: "6px", textAlign: "center", fontSize: "12px" }}>
              Customers Signature
            </div>
            <div style={{ width: "45%", borderTop: "1px dotted #666", paddingTop: "6px", textAlign: "center", fontSize: "12px" }}>
              Driver/Operator Name
            </div>
          </div>

          {/* ── Footer — Invoice Footer field from company settings ───── */}
          {WorkOrderDataList?.company?.companyFooterDetails &&
            WorkOrderDataList.company.companyFooterDetails.trim() !== "" && (
              <div style={{
                borderTop: "2px solid #000000",
                padding: "12px 32px",
                fontSize: "12px",
                color: "#333",
                fontWeight: "bold",
                textTransform: "uppercase",
                position: "relative",
                zIndex: 1,
              }}>
                {WorkOrderDataList.company.companyFooterDetails.split("\n").map((line, i) => (
                  <span key={i}>{line}<br /></span>
                ))}
              </div>
            )}
        </div>
      </div>
    </Box>
  );
};

export default ViewInvoice;
